// ─── FerMat — Shared UI Components ──────────────────────────────
import { useState, useEffect, useRef } from 'react';
import { SECTIONS, THEME, SPACING, RADIUS, FONT_SIZE, FONT_WEIGHT, DURATION, TOUCH_MIN } from '@data';
import { getSectionColor } from '@utils/helpers.js';
import { IconCheck, IconX, IconSearch, IconVolume, IconChevronRight } from '@components/icons';

// ── Pill (toggleable filter button) ──────────────────────────────────────────
// Modern colour-aware pill. Inactive pills wear a soft tint of their own colour
// so a horizontal row reads as a coloured map (each section keeps its identity).
// Active pill bumps to a gradient fill + glow shadow for a clear focal point.
//
// Props:
//   - label / children: the visible text
//   - isActive: highlighted state
//   - color: the pill's identity colour (defaults to logo teal)
//   - icon: optional emoji/string shown before the label
//   - isDark: dark-mode toggle (callers already pass this)
export function Pill({ label, isActive = false, color, icon, onClick, children, isDark = false }) {
  const tint = color || '#0D9488';
  const text = children || label;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      style={{
        padding: icon ? '7px 14px 7px 10px' : '7px 14px',
        minHeight: 40,
        borderRadius: RADIUS.full,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: FONT_SIZE.sm,
        fontWeight: isActive ? FONT_WEIGHT.bold : FONT_WEIGHT.semibold,
        transition: `all ${DURATION.normal}`,
        border: isActive ? 'none' : `1px solid ${tint}${isDark ? '33' : '22'}`,
        background: isActive
          ? `linear-gradient(135deg, ${tint} 0%, ${shade(tint, -12)} 100%)`
          : (isDark ? `${tint}1A` : `${tint}10`),
        color: isActive ? '#fff' : (isDark ? tint : shade(tint, -70)),
        boxShadow: isActive
          ? `0 4px 12px ${tint}55, 0 1px 2px ${tint}33`
          : 'none',
        whiteSpace: 'nowrap',
        letterSpacing: '0.015em',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {icon && (
        <span aria-hidden="true" style={{
          fontSize: '0.95em',
          lineHeight: 1,
          opacity: isActive ? 1 : 0.85,
        }}>
          {icon}
        </span>
      )}
      {text}
    </button>
  );
}

// Darken a hex colour for the gradient endpoint. Cheap & deterministic.
function shade(hex, amount = -10) {
  const c = hex.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(c.substring(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(c.substring(2, 4), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(c.substring(4, 6), 16) + amount));
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ label, color, bgColor }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: `${SPACING.xs}px ${SPACING.sm}px`,
      borderRadius: RADIUS.full,
      fontSize: FONT_SIZE.xs,
      fontWeight: FONT_WEIGHT.bold,
      letterSpacing: '0.03em',
      background: bgColor || 'rgba(0,0,0,0.08)',
      color: color || '#555',
    }}>
      {label}
    </span>
  );
}

// ── ConceptRow (list row with visual thumbnail) ──────────────────────────────
// Uses CSS content-visibility for free off-screen virtualization on modern browsers
// (Chrome 85+, Safari 18+, Firefox 125+, Edge 85+). Older browsers render normally.
export function ConceptRow({ concept, theme, isDark, onClick, isLast = false, VisualComponent }) {
  const t = theme || THEME.light;
  const colors = getSectionColor(concept.s, isDark);
  const hasVisual = concept.visual && VisualComponent;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: SPACING.md,
        width: '100%', padding: `10px ${SPACING.lg}px`,
        minHeight: 64,
        background: 'transparent',
        border: 'none', borderBottom: isLast ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
        WebkitTapHighlightColor: 'transparent',
        // Browser-level virtualization: skip layout/paint of off-screen rows.
        contentVisibility: 'auto',
        containIntrinsicSize: '64px',
      }}
    >
      {/* Thumbnail — concept visual or section emoji */}
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: colors.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, overflow: 'hidden',
      }}>
        {hasVisual
          ? <VisualComponent visual={concept.visual} theme={t} size={36} />
          : <span style={{ fontSize: '1.3rem' }}>{SECTIONS[concept.s]?.icon}</span>
        }
      </div>
      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.semibold,
          color: t.text, lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {concept.ku}
        </div>
        <div style={{
          fontSize: FONT_SIZE.sm, color: t.textMuted,
          fontWeight: FONT_WEIGHT.normal, lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {concept.tr}{concept.en ? ` · ${concept.en}` : ''}
        </div>
      </div>
      {/* Level badge + chevron */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexShrink: 0 }}>
        <span style={{
          fontSize: FONT_SIZE.xs, color: t.textMuted,
          fontWeight: FONT_WEIGHT.medium,
        }}>
          {concept.lv}
        </span>
        <IconChevronRight size={14} color={isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'} />
      </div>
    </button>
  );
}

// ── PageContainer (unified page wrapper — 520px max, centered) ───────────────
export function PageContainer({ children, noPadTop = false }) {
  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{
        maxWidth: 520, margin: '0 auto',
        padding: `${noPadTop ? 0 : 24}px 16px 80px`,
        animation: 'fadeIn 0.2s ease-out',
      }}>
        {children}
      </div>
    </div>
  );
}

// ── SectionCard (grouped list container — 16px radius) ───────────────────────
export function SectionCard({ children, theme, style = {} }) {
  const t = theme || THEME.light;
  return (
    <div style={{
      background: t.surface,
      borderRadius: 16,
      border: `1px solid ${t.border}`,
      overflow: 'hidden',
      ...style,
    }}>
      {children}
    </div>
  );
}

// Keep ListSection as alias for backward compat
export const ListSection = SectionCard;

// ── SectionTitle (uppercase group label) ─────────────────────────────────────
export function SectionTitle({ children, theme }) {
  const t = theme || THEME.light;
  return (
    <div style={{
      fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
      color: t.textMuted, textTransform: 'uppercase',
      letterSpacing: '0.06em',
      padding: '24px 0 8px',
    }}>
      {children}
    </div>
  );
}

// ── ActionRow (navigation row — icon + text + desc + chevron) ────────────────
export function ActionRow({ icon: Icon, iconColor, label, desc, onClick, theme, isDark, isLast = false }) {
  const t = theme || THEME.light;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        width: '100%', padding: '12px 16px', minHeight: 56,
        background: 'transparent', border: 'none',
        borderBottom: isLast ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
        WebkitTapHighlightColor: 'transparent',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: (iconColor || '#0D9488') + (isDark ? '20' : '10'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={20} color={iconColor || '#0F4C5C'} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, color: t.text }}>{label}</div>
        {desc && <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, marginTop: 1 }}>{desc}</div>}
      </div>
      <IconChevronRight size={16} color={isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'} />
    </button>
  );
}

// ── StatRow (label + value row) ──────────────────────────────────────────────
export function StatRow({ label, value, theme, isDark, isLast = false }) {
  const t = theme || THEME.light;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 16px',
      borderBottom: isLast ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
    }}>
      <span style={{ fontSize: FONT_SIZE.base, color: t.text }}>{label}</span>
      <span style={{ fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.semibold, color: t.textSecondary }}>{value}</span>
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, theme, onClick, style = {}, isInteractive = false }) {
  const [isHovered, setIsHovered] = useState(false);
  const t = theme || THEME.light;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => isInteractive && setIsHovered(true)}
      onMouseLeave={() => isInteractive && setIsHovered(false)}
      style={{
        background: t.surface,
        borderRadius: RADIUS.lg,
        boxShadow: isHovered ? t.cardShadowHover : t.cardShadow,
        border: '1px solid ' + t.border,
        transition: `all ${DURATION.normal} ease`,
        transform: isInteractive && isHovered ? 'translateY(-2px)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── ProgressBar ───────────────────────────────────────────────────────────────
export function ProgressBar({ value, max, color, bgColor, height = SPACING.sm }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{
      background: bgColor || 'rgba(0,0,0,0.08)',
      borderRadius: RADIUS.full,
      height,
      overflow: 'hidden',
    }}>
      <div style={{
        height: '100%',
        width: pct + '%',
        borderRadius: RADIUS.full,
        background: color || '#0F4C5C',
        transition: `width ${DURATION.slow} ease-out`,
      }} />
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
// Accessibility:
//   - role="dialog" + aria-modal so screen readers announce as modal
//   - aria-label can be passed by caller; defaults to a generic value
//   - Auto-focuses the first focusable element when opened
//   - Restores focus to previously focused element on close
//   - Escape key closes
export function Modal({ isOpen, onClose, theme, children, maxWidth = 420, ariaLabel = 'Pencereya pê re têkilî' }) {
  const t = theme || THEME.light;
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Keyboard: Escape closes
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Focus management: capture current focus, move into dialog, restore on close
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement;
    const dialog = dialogRef.current;
    if (dialog) {
      const firstFocusable = dialog.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      (firstFocusable || dialog).focus();
    }
    return () => {
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: SPACING.lg + 'px',
        animation: `fadeIn 0.2s ease-out`,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
        style={{
          background: t.surface,
          borderRadius: RADIUS.xl,
          padding: `${SPACING.xl}px ${SPACING.xl - 4}px`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          maxWidth: maxWidth + 'px',
          width: '100%',
          animation: `slideUp ${DURATION.normal} cubic-bezier(0.22,0.61,0.36,1)`,
          maxHeight: '90dvh',
          overflowY: 'auto',
          outline: 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── ToastContainer ────────────────────────────────────────────────────────────
// Logo-palette toasts. Each type gets a gradient + matching shadow so the
// notification visually echoes the in-app pill / button language.
const TOAST_ICONS = { success: IconCheck, error: IconX, info: IconSearch };

const TOAST_STYLES = {
  success: {
    bg:     'linear-gradient(135deg, #15803D 0%, #0D9488 100%)',  // logo green→teal
    shadow: 'rgba(21,128,61,0.35)',
  },
  error: {
    bg:     'linear-gradient(135deg, #DC2626 0%, #C2410C 100%)',  // red→coral
    shadow: 'rgba(220,38,38,0.35)',
  },
  info: {
    bg:     'linear-gradient(135deg, #0D9488 0%, #0F4C5C 100%)',  // logo teal→dark
    shadow: 'rgba(13,148,136,0.35)',
  },
  warning: {
    bg:     'linear-gradient(135deg, #D97706 0%, #EA580C 100%)',  // amber→coral
    shadow: 'rgba(217,119,6,0.35)',
  },
};

export function ToastContainer({ toasts, theme }) {
  const t = theme || THEME.light;
  return (
    <div style={{
      position: 'fixed',
      top: SPACING.lg,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 2000,
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING.sm,
      alignItems: 'center',
      pointerEvents: 'none',
      maxWidth: 'calc(100vw - 32px)',
    }}>
      {toasts.map(toast => {
        const ToastIcon = TOAST_ICONS[toast.type];
        const style = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
        return (
          <div
            key={toast.id}
            role="status"
            aria-live="polite"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: style.bg,
              color: '#fff',
              padding: '10px 18px',
              borderRadius: 9999,
              fontSize: FONT_SIZE.base,
              fontWeight: FONT_WEIGHT.semibold,
              boxShadow: `0 6px 18px ${style.shadow}, 0 1px 3px rgba(0,0,0,0.15)`,
              border: '1px solid rgba(255,255,255,0.15)',
              animation: `fadeInUp 0.3s ease-out`,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              letterSpacing: '0.01em',
            }}
          >
            {ToastIcon && <ToastIcon size={16} color="#fff" />}
            {toast.message}
          </div>
        );
      })}
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, message, actionLabel, onAction, theme }) {
  const t = theme || THEME.light;
  return (
    <div style={{ textAlign: 'center', padding: `${SPACING.xxl + 8}px ${SPACING.xl - 4}px` }}>
      <div style={{ fontSize: 44, marginBottom: SPACING.lg - 2 }}>{icon || '🔍'}</div>
      <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.bold, color: t.text, marginBottom: SPACING.sm }}>
        {title}
      </div>
      {message && (
        <div style={{ fontSize: FONT_SIZE.base, color: t.textSecondary, marginBottom: SPACING.xl - 4, lineHeight: 1.5 }}>
          {message}
        </div>
      )}
      {actionLabel && (
        <Button variant="primary" onClick={onAction} theme={t}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

// ── SectionTag ────────────────────────────────────────────────────────────────
export function SectionTag({ sectionId, isDark, size = 'sm' }) {
  const section = SECTIONS[sectionId];
  if (!section) return null;
  const colors = getSectionColor(sectionId, isDark);
  const isSmall = size === 'sm';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: SPACING.xs,
      padding: isSmall ? `${SPACING.xs}px ${SPACING.sm}px` : `${SPACING.xs + 1}px ${SPACING.md}px`,
      borderRadius: RADIUS.full,
      background: colors.bg,
      color: colors.text,
      fontSize: isSmall ? '0.72rem' : FONT_SIZE.sm,
      fontWeight: FONT_WEIGHT.semibold,
    }}>
      <span>{section.icon}</span>
      {!isSmall && section.short}
    </span>
  );
}

// ── ScoreCircle ───────────────────────────────────────────────────────────────
export function ScoreCircle({ score, total, theme, size = 120 }) {
  const t = theme || THEME.light;
  const pct = total > 0 ? Math.round((score / total) * 100) : 0;
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDash = (pct / 100) * circumference;
  const color = pct >= 80 ? t.success : pct >= 50 ? t.warning : t.error;
  const cx = size / 2, cy = size / 2;

  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke={t.border} strokeWidth="8" />
        <circle
          cx={cx} cy={cy} r={radius}
          fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={strokeDash + ' ' + circumference}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: `stroke-dasharray 0.8s ease-out` }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: size < 100 ? FONT_SIZE.lg : FONT_SIZE.xl, fontWeight: FONT_WEIGHT.extrabold, color }}>
          {pct}%
        </span>
        <span style={{ fontSize: '0.72rem', color: t.textMuted, fontWeight: FONT_WEIGHT.semibold }}>
          {score}/{total}
        </span>
      </div>
    </div>
  );
}

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({ children, onClick, variant = 'primary', theme, disabled = false, style = {} }) {
  const t = theme || THEME.light;
  const [isHovered, setIsHovered] = useState(false);

  const variants = {
    primary: {
      background: disabled ? t.textMuted : (isHovered ? t.accentHover : t.accent),
      color: t.textOnAccent,
      border: 'none',
    },
    secondary: {
      background: disabled ? t.border : (isHovered ? t.primarySoft : 'transparent'),
      color: t.primary,
      border: '1.5px solid ' + t.primary,
    },
    ghost: {
      background: isHovered ? t.surfaceHover : 'transparent',
      color: t.text,
      border: '1px solid ' + t.border,
    },
    success: {
      background: disabled ? t.textMuted : (isHovered ? t.success : t.success),
      color: '#fff',
      border: 'none',
    },
  };

  const v = variants[variant] || variants.primary;

  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      style={{
        padding: `${SPACING.md}px ${SPACING.xl + 4}px`,
        minHeight: TOUCH_MIN,
        borderRadius: RADIUS.full,
        fontSize: FONT_SIZE.base,
        fontWeight: FONT_WEIGHT.semibold,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        transition: `all ${DURATION.normal}`,
        opacity: disabled ? 0.6 : 1,
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        outline: 'none',
        ...v,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ── SpeakButton ──────────────────────────────────────────────────────────────
export function SpeakButton({ text, speak, isSpeaking, theme, size = 32 }) {
  const t = theme || THEME.light;
  if (!text || !speak) return null;

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); speak(text); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); speak(text); } }}
      aria-label={'Bibêje: ' + text}
      title="Bibêje"
      style={{
        // Visual size stays `size`; the hit area never drops below 40px.
        width: Math.max(size, 40), height: Math.max(size, 40),
        borderRadius: '50%',
        border: 'none',
        background: isSpeaking ? t.primarySoft : 'transparent',
        cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        transition: `all ${DURATION.fast}`,
        animation: isSpeaking ? 'pulse 1s ease-in-out infinite' : 'none',
        WebkitTapHighlightColor: 'transparent',
        padding: 0,
      }}
    >
      <IconVolume size={Math.max(size * 0.55, 16)} color={isSpeaking ? t.primary : t.textMuted} />
    </span>
  );
}
